import React from 'react';
import { Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { V4_TABLE } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import { Loader } from '../../elements';
import NoDataMessage from '../shared/NoDataMessage';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { FeedRegistration, feedRegistrationDayInstant, formatFeedAmount } from '../../models/feed-registration';
import { LoaderSize } from '../../constants/constants';
import FeedRegistrationAnimalsPanel from './FeedRegistrationAnimalsPanel';

export interface FeedRegistrationsTableProps {
  registrations: FeedRegistration[];
  isBusy: boolean;
  loaded: boolean;
  /** Off on a single pen, where every row would repeat the same number. */
  showPen?: boolean;
  emptyText?: string;
}

/**
 * The rows behind both feed views. There is no time column: a registration carries the day the feed
 * was handed out, not a timestamp - the farmer can back-date it, so the moment it was typed says
 * nothing useful.
 *
 * A row opens onto the animals its feed was split across.
 */
const FeedRegistrationsTable: React.FC<FeedRegistrationsTableProps> = ({
  registrations,
  isBusy,
  loaded,
  showPen,
  emptyText
}) => {
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();
  const [openIds, setOpenIds] = React.useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const columnCount = showPen ? 5 : 4;

  if (isBusy && !loaded) return <Loader isLoading size={LoaderSize.MEDIUM} />;

  if (loaded && registrations.length === 0) {
    return <NoDataMessage text={emptyText ?? t('f-feed-reg-empty')} description={t('f-feed-reg-empty-info')} />;
  }

  return (
    <TableContainer className="overflow-x-auto">
      <Table size="small" aria-label="feed registrations table" id="feed-registrations-table" sx={V4_TABLE}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>{t('f-com-date')}</TableCell>
            {showPen && <TableCell>{t('f-loc-pen')}</TableCell>}
            <TableCell>{t('f-feed-reg-name')}</TableCell>
            <TableCell align="right">{t('f-feed-reg-amount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {registrations.map((registration) => {
            const open = openIds.has(registration.id);

            return (
              <React.Fragment key={registration.id}>
                <TableRow hover className="cursor-pointer" onClick={() => toggle(registration.id)}>
                  <TableCell padding="checkbox">
                    <IconButton
                      size="small"
                      aria-expanded={open}
                      aria-label={t('f-feed-reg-show-animals', { defaultValue: 'Show animals' })}
                    >
                      {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{formatDate(feedRegistrationDayInstant(registration.date))}</TableCell>
                  {showPen && (
                    <TableCell>
                      <Link
                        to={`/locations/${registration.locationId}/feed`}
                        onClick={(event) => event.stopPropagation()}
                        className="text-brand hover:underline"
                      >
                        {registration.locationNumber}
                      </Link>
                    </TableCell>
                  )}
                  <TableCell className="font-medium">{registration.feedName}</TableCell>
                  <TableCell align="right">{formatFeedAmount(registration.amountGrams)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                      <div className="pl-12">
                        <FeedRegistrationAnimalsPanel registrationId={registration.id} />
                      </div>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FeedRegistrationsTable;
